import React from 'react'
import {
  AlignItems,
  Box,
  Color,
  Display,
  FlexDirection,
  FontWeight,
  Icon,
  IconNames,
  IconVariant,
  JustifyContent,
  Space,
  Text,
  withCitrusTheme,
} from '@gousto-internal/citrus-react'

import type { DashboardActionData } from '@library/api-customer-engagement-experience'

const ICON_MAP: Record<string, IconNames> = {
  resub: 'resub',
}

const FALLBACK_ICON: IconNames = 'resub'

const RESTING_SHADOW = '0px 0px 2px 0px #2129300D, 0px 1px 4px 0px #2129301A'
const HOVER_SHADOW =
  '0 12px 16px -4px rgba(33, 41, 48, 0.06), 0 4px 6px -2px rgba(33, 41, 48, 0.03)'

// Bold title for the inline layout, with a little room before the subtitle starts.
const InlineTitle = withCitrusTheme(Text, (theme) => ({
  paddingRight: `${theme.spacing(1)}px`,
}))

// Subtitle for the inline layout: an inline-block so it stays together — it drops onto its own
// line below the bold title when it no longer fits beside it, rather than wrapping mid-sentence.
const InlineSubtitle = withCitrusTheme(Text, () => ({
  display: 'inline-block',
  maxWidth: '100%',
}))

type PromptCardProps = React.ComponentPropsWithoutRef<'button'> & {
  backgroundColor: Color
  backgroundWhiteMix: number
  flat: boolean
}

// Strips the styling-only props so they don't reach the DOM button.
function BasePromptCard({
  backgroundColor: _backgroundColor,
  backgroundWhiteMix: _backgroundWhiteMix,
  flat: _flat,
  ...props
}: PromptCardProps) {
  // eslint-disable-next-line react/jsx-props-no-spreading
  return <button {...props} />
}

const PromptCard = withCitrusTheme(BasePromptCard, (theme, props: PromptCardProps) => ({
  appearance: 'none',
  border: 'none',
  width: '100%',
  textAlign: 'left',
  cursor: 'pointer',
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  backgroundColor: props.backgroundWhiteMix
    ? `color-mix(in srgb, ${theme.color(Color.White)} ${props.backgroundWhiteMix}%, ${theme.color(
        props.backgroundColor,
      )})`
    : theme.color(props.backgroundColor),
  borderRadius: '4px',
  padding: `${theme.spacing(4)}px`,
  boxShadow: props.flat ? 'none' : RESTING_SHADOW,
  transition: 'box-shadow 150ms ease-in-out',
  '&:hover': { boxShadow: HOVER_SHADOW },
  '&:focus-visible': {
    outline: `2px solid ${theme.color(Color.Secondary_400)}`,
    outlineOffset: '2px',
  },
}))

export type RegularDeliveryPromptVariant = 'dashboard' | 'orderConfirmation'

type VariantStyle = {
  backgroundColor: Color
  // Percentage of white (0–100) blended into the background via color-mix, for a lighter shade the
  // Colour tokens don't provide (order confirmation's lavender = 48% white over Secondary_50).
  backgroundWhiteMix: number
  // Coloured circle behind the icon (order confirmation only).
  iconBackgroundColor?: Color
  // Drop the resting/hover shadow for a flat card (order confirmation).
  flat: boolean
  // Flow the title and subtitle inline (wrapping on narrow widths) instead of the dashboard's
  // stacked two-line layout.
  inline: boolean
}

const STYLE_BY_VARIANT: Record<RegularDeliveryPromptVariant, VariantStyle> = {
  dashboard: {
    backgroundColor: Color.White,
    backgroundWhiteMix: 0,
    flat: false,
    inline: false,
  },
  orderConfirmation: {
    backgroundColor: Color.Secondary_50,
    backgroundWhiteMix: 48,
    iconBackgroundColor: Color.Secondary_50,
    flat: true,
    inline: true,
  },
}

export type RegularDeliveryPromptProps = {
  data: DashboardActionData
  onAction: (intent: string) => void
  variant?: RegularDeliveryPromptVariant
}

export const RegularDeliveryPrompt = ({
  data,
  onAction,
  variant = 'dashboard',
}: RegularDeliveryPromptProps) => {
  const { backgroundColor, backgroundWhiteMix, iconBackgroundColor, flat, inline } =
    STYLE_BY_VARIANT[variant]
  const { title, subtitle, icon, intent } = data
  const iconName = ICON_MAP[icon] ?? FALLBACK_ICON

  const handleClick = () => {
    onAction(intent)
  }

  return (
    <PromptCard
      type="button"
      aria-label={title}
      data-intent={intent}
      onClick={handleClick}
      backgroundColor={backgroundColor}
      backgroundWhiteMix={backgroundWhiteMix}
      flat={flat}
    >
      <Box display={Display.Flex} flexDirection={FlexDirection.Row} alignItems={AlignItems.Center}>
        <Box
          color={Color.Secondary_400}
          display={Display.Flex}
          alignItems={AlignItems.Center}
          justifyContent={JustifyContent.Center}
          flexShrink={0}
          bg={iconBackgroundColor}
          borderRadius={iconBackgroundColor ? 'round' : undefined}
          width={iconBackgroundColor ? '40px' : undefined}
          height={iconBackgroundColor ? '40px' : undefined}
        >
          <Icon aria-hidden="true" name={iconName} size={8} variant={IconVariant.Inherit} />
        </Box>
        <Space size={3} direction="horizontal" />
        {inline ? (
          <Box>
            <InlineTitle
              display={Display.Inline}
              color={Color.ColdGrey_800}
              fontWeight={FontWeight.Bold}
              size={4}
            >
              {title}
            </InlineTitle>{' '}
            <InlineSubtitle color={Color.ColdGrey_600} size={4}>
              {subtitle}
            </InlineSubtitle>
          </Box>
        ) : (
          <Box>
            <Text color={Color.ColdGrey_800} fontWeight={FontWeight.Bold} size={4}>
              {title}
            </Text>
            <Text color={Color.ColdGrey_600} size={2}>
              {subtitle}
            </Text>
          </Box>
        )}
      </Box>
      <Box color={Color.Secondary_400} display={Display.Flex} alignItems={AlignItems.Center}>
        <Icon aria-hidden="true" name="arrow_right" size={6} variant={IconVariant.Inherit} />
      </Box>
    </PromptCard>
  )
}
